// Profile: completion ring, soft prompt (after pull 2), and the full profile form.
// Saves to /api/me (hub `profiles` is the source of truth; identity from the session cookie).
import { useState, useRef } from 'react';
import {
  ROLES, OPTIONS, OPTIONAL_FIELDS, profileCompletion, validateProfile, formatPhone,
} from './lib/profile';

const C = {
  navy: '#0f172a', panel: '#111c33', line: 'rgba(248,250,252,0.12)', text: '#f8fafc',
  sub: '#94a3b8', faint: '#64748b', amber: '#f59e0b', green: '#22c55e', red: '#fca5a5',
};

export function CompletionRing({ percent = 0, size = 40, stroke = 4, children, title }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, percent));
  const color = pct >= 100 ? C.green : C.amber;
  return (
    <div title={title} style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
      <svg width={size} height={size} style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(248,250,252,0.12)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 400ms ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: stroke + 2, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}

export function Avatar({ url, first, last, size = 32 }) {
  const [broken, setBroken] = useState(false);
  const initials = `${(first || '?')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
  if (url && !broken) {
    return <img src={url} alt="" onError={() => setBroken(true)} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#1e293b', color: C.amber, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.38 }}>
      {initials}
    </div>
  );
}

// Header chip: avatar inside the completion ring.
export function ProfileChip({ me, onClick }) {
  if (!me?.user) return null;
  const pct = me.completion?.percent ?? 0;
  return (
    <button onClick={onClick} title={`Profile ${pct}% complete`}
      style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', border: `1px solid ${C.line}`, borderRadius: 999, padding: '4px 12px 4px 4px', cursor: 'pointer', color: C.text }}>
      <CompletionRing percent={pct} size={36} stroke={3}>
        <Avatar url={me.user.avatarUrl} first={me.user.firstName} last={me.user.lastName} size={28} />
      </CompletionRing>
      <span style={{ fontSize: 12, color: pct >= 100 ? C.green : C.sub, fontWeight: 600 }}>{pct}%</span>
    </button>
  );
}

// Soft gate after pull 2 — always dismissible.
export function ProfilePrompt({ me, onOpen, onLater }) {
  const pct = me?.completion?.percent ?? 0;
  const missing = me?.completion?.missingRequired || [];
  return (
    <div style={overlay}>
      <div style={{ ...card, maxWidth: 460, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <CompletionRing percent={pct} size={96} stroke={7}>
            <span style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{pct}%</span>
          </CompletionRing>
        </div>
        <h3 style={{ margin: '0 0 8px', fontSize: 20, color: C.text }}>Finish your profile?</h3>
        <p style={{ margin: '0 0 12px', color: C.sub, fontSize: 14, lineHeight: 1.5 }}>
          You&apos;ve run {me?.pulls ?? 2} lookups. Take a minute to fill in your profile so we know who&apos;s using Licensr and can build the next tools around how your agency works.
        </p>
        {missing.length > 0 && (
          <p style={{ margin: '0 0 20px', color: C.faint, fontSize: 12 }}>Still needed: {missing.join(', ')}</p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onLater} style={btnGhost}>Maybe later</button>
          <button onClick={onOpen} style={btnPrimary}>Complete profile</button>
        </div>
      </div>
    </div>
  );
}

async function resizeToDataUrl(file, max = 256) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const side = Math.min(img.width, img.height);
  const canvas = document.createElement('canvas');
  canvas.width = max; canvas.height = max;
  canvas.getContext('2d').drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, max, max);
  URL.revokeObjectURL(img.src);
  return canvas.toDataURL('image/jpeg', 0.85);
}

export default function ProfileView({ me, states = [], onClose, onSaved, linkedinEnabled = false }) {
  const p0 = me?.profile || {};
  const [form, setForm] = useState(() => ({
    ...p0,
    work_phone: formatPhone(p0.work_phone),
    mobile_phone: formatPhone(p0.mobile_phone),
    states_licensed: (p0.states_licensed || []).join(', '),
  }));
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showOptional, setShowOptional] = useState(true);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => {
    const cur = Array.isArray(f[k]) ? f[k] : [];
    return { ...f, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });

  const live = profileCompletion({
    ...form,
    states_licensed: String(form.states_licensed || '').split(',').map((s) => s.trim()).filter(Boolean),
    avatar_url: me?.profile?.avatar_url,
  });

  const save = async () => {
    setMsg('');
    const payload = { ...form, states_licensed: String(form.states_licensed || '') };
    const errs = validateProfile(payload);
    setErrors(errs);
    if (Object.keys(errs).length) { setMsg('Please fix the highlighted fields.'); return; }
    setSaving(true);
    try {
      const r = await fetch('/api/me', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ profile: payload }) });
      const d = await r.json();
      if (!r.ok) { setErrors(d.fields || {}); setMsg(d.error || 'Save failed.'); return; }
      onSaved?.(d);
      setMsg('Saved.');
      onClose?.();
    } catch {
      setMsg('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg('');
    try {
      const dataUrl = await resizeToDataUrl(file);
      const r = await fetch('/api/me', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ photo: dataUrl }) });
      const d = await r.json();
      if (!r.ok) setMsg(d.error || 'Upload failed.');
      else onSaved?.(d);
    } catch {
      setMsg("Couldn't read that image.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const field = (k, label, props = {}) => (
    <div style={fg}>
      <label style={lbl}>{label}</label>
      <input value={form[k] || ''} onChange={(e) => set(k, e.target.value)} style={{ ...inp, ...(errors[k] ? inpErr : {}) }} {...props} />
      {errors[k] && <span style={errTxt}>{errors[k]}</span>}
    </div>
  );
  const select = (k, label, opts) => (
    <div style={fg}>
      <label style={lbl}>{label}</label>
      <select value={form[k] || ''} onChange={(e) => set(k, e.target.value)} style={inp}>
        <option value="">Select…</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  const chips = (k, label, opts) => (
    <div style={{ ...fg, gridColumn: '1 / -1' }}>
      <label style={lbl}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {opts.map((o) => {
          const on = (form[k] || []).includes(o);
          return (
            <button key={o} type="button" onClick={() => toggle(k, o)}
              style={{ padding: '6px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer', border: `1px solid ${on ? C.amber : C.line}`, background: on ? 'rgba(245,158,11,0.15)' : 'transparent', color: on ? C.amber : C.sub }}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );

  const avatarNote = { upload: 'Your uploaded photo', gravatar: 'Using your Gravatar', linkedin: 'From LinkedIn' }[me?.profile?.avatar_source] || 'No photo yet';

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={{ ...card, maxWidth: 760, maxHeight: '90vh', overflowY: 'auto', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <CompletionRing percent={live.percent} size={72} stroke={5}>
            <Avatar url={me?.profile?.avatar_url} first={form.first_name} last={form.last_name} size={58} />
          </CompletionRing>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 20, color: C.text }}>Your profile</h3>
            <div style={{ fontSize: 13, color: live.percent >= 100 ? C.green : C.sub, marginTop: 4 }}>{live.percent}% complete</div>
            <div style={{ fontSize: 12, color: C.faint, marginTop: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span>{avatarNote}</span>
              <button type="button" onClick={() => fileRef.current?.click()} style={linkBtn} disabled={uploading}>
                {uploading ? 'Uploading…' : me?.profile?.avatar_source === 'upload' ? 'Change photo' : 'Upload photo'}
              </button>
              {linkedinEnabled && me?.profile?.avatar_source !== 'linkedin' && (
                <a href="/api/auth?action=linkedin_start&mode=link" style={{ ...linkBtn, textDecoration: 'none' }}>Use my LinkedIn photo</a>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} style={{ display: 'none' }} />
            </div>
          </div>
          <button onClick={onClose} style={{ ...linkBtn, fontSize: 22, color: C.sub }} aria-label="Close">×</button>
        </div>

        <div style={section}>Required</div>
        <div style={grid}>
          {field('first_name', 'First name')}
          {field('last_name', 'Last name')}
          <div style={fg}>
            <label style={lbl}>Work email</label>
            <input value={me?.profile?.email || ''} readOnly style={{ ...inp, opacity: 0.6 }} />
            <span style={hint}>{me?.user?.emailVerified ? 'Confirmed' : 'Confirmed when you click a link we email you'}</span>
          </div>
          {field('alternate_email', 'Alternate email', { type: 'email', placeholder: 'Personal is fine' })}
          {field('work_phone', 'Work phone', { type: 'tel', placeholder: '(810) 555-0100' })}
          {field('mobile_phone', 'Mobile phone', { type: 'tel', placeholder: '(810) 555-0100' })}
          {field('company', 'Agency name')}
          {field('agency_website', 'Agency website', { placeholder: 'youragency.com' })}
          <div style={fg}>
            <label style={lbl}>Role</label>
            <select value={form.role || ''} onChange={(e) => set('role', e.target.value)} style={{ ...inp, ...(errors.role ? inpErr : {}) }}>
              <option value="">Select…</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {form.role === 'Other' ? field('role_other', 'Your role') : (
            <div style={fg}>
              <label style={lbl}>Agency state</label>
              <select value={form.agency_state || ''} onChange={(e) => set('agency_state', e.target.value)} style={inp}>
                <option value="">Select…</option>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }}>{field('linkedin_url', 'LinkedIn profile', { placeholder: 'linkedin.com/in/yourname' })}</div>
        </div>

        <button type="button" onClick={() => setShowOptional((v) => !v)} style={{ ...section, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 24 }}>
          Optional {showOptional ? '▾' : '▸'}
        </button>
        {showOptional && (
          <>
            <p style={{ ...hint, margin: '4px 0 12px' }}>These help us build tools around how agencies actually work. We use them in aggregate and never sell them.</p>
            <div style={grid}>
              {OPTIONAL_FIELDS.map((f) => {
                if (f.type === 'multi') return <div key={f.key} style={{ gridColumn: '1 / -1' }}>{chips(f.key, f.label, OPTIONS[f.key])}</div>;
                if (f.type === 'select') return <div key={f.key}>{select(f.key, f.label, OPTIONS[f.key])}</div>;
                return <div key={f.key}>{field(f.key, f.label, { placeholder: f.placeholder })}</div>;
              })}
            </div>
          </>
        )}

        {msg && <div style={{ marginTop: 16, fontSize: 13, color: msg === 'Saved.' ? C.green : C.red }}>{msg}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const card = { width: '100%', background: C.panel, border: `1px solid ${C.line}`, borderRadius: 16, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 };
const fg = { display: 'flex', flexDirection: 'column', gap: 6 };
const lbl = { fontSize: 12, fontWeight: 500, color: '#cbd5e1' };
const inp = { background: 'rgba(15,23,42,0.8)', border: `1px solid ${C.line}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, color: C.text, outline: 'none' };
const inpErr = { borderColor: 'rgba(220,38,38,0.7)' };
const errTxt = { fontSize: 11, color: C.red };
const hint = { fontSize: 11, color: C.faint };
const section = { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.amber, margin: '0 0 12px', display: 'block' };
const btnPrimary = { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: C.navy, border: 'none', padding: '11px 20px', fontSize: 14, fontWeight: 600, borderRadius: 8, cursor: 'pointer' };
const btnGhost = { background: 'transparent', color: C.sub, border: `1px solid ${C.line}`, padding: '11px 20px', fontSize: 14, borderRadius: 8, cursor: 'pointer' };
const linkBtn = { background: 'none', border: 'none', color: C.amber, cursor: 'pointer', fontSize: 12, padding: 0 };
